/**
 * 🗄️ Sistema de Caché LRU (Least Recently Used) para IndexedDB
 * 
 * Implementa límites de tamaño y evicción automática para evitar que IndexedDB
 * crezca ilimitadamente, especialmente crítico para escalar a 10k+ tiendas.
 * 
 * Estrategia:
 * 1. Cada item tiene un `last_accessed` timestamp
 * 2. Cuando se excede el límite, se eliminan los items más antiguos (LRU)
 * 3. Los límites son por store y por tenant
 */

import { IDBPDatabase } from 'idb';
import { logDebug, logWarn } from '@/lib/logger';
import type { CachedRecord } from '@/lib/db';

/**
 * Configuración de límites por store
 */
const CACHE_LIMITS = {
  products: 1000,      // Máximo 1000 productos por tenant
  orders: 500,         // Máximo 500 órdenes por tenant
  orderItems: 2000,    // Máximo 2000 items de orden por tenant
  tables: 200,         // Máximo 200 mesas por branch
  categories: 100,     // Máximo 100 categorías por tenant
  modifiers: 500,      // Máximo 500 modificadores por tenant
} as const;

type StoreName = keyof typeof CACHE_LIMITS;

/**
 * Agregar timestamp de último acceso a un item
 */
export function addAccessTimestamp(item: CachedRecord): CachedRecord {
  return {
    ...item,
    _last_accessed: Date.now(),
  };
}

/**
 * Obtener límite para un store
 */
function getStoreLimit(storeName: StoreName): number {
  return CACHE_LIMITS[storeName] || 1000;
}

/**
 * Evictar items más antiguos (LRU) de un store
 * 
 * @param db Instancia de IndexedDB
 * @param storeName Nombre del store
 * @param currentCount Cantidad actual de items
 * @param limit Límite máximo permitido
 * @param tenantId ID del tenant (para filtrar)
 */
export async function evictLRUItems(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: IDBPDatabase<any>,
  storeName: StoreName,
  currentCount: number,
  limit: number,
  tenantId?: string
): Promise<number> {
  if (currentCount <= limit) {
    return 0; // No hay exceso
  }

  const excess = currentCount - limit;
  logWarn(`[LRU] Store '${storeName}' excede límite: ${currentCount} > ${limit}. Evictando ${excess} items más antiguos.`);

  try {
    // Obtener todos los items con su timestamp de acceso
    const allItems = await db.getAll(storeName);
    
    // Filtrar por tenant si se proporciona
    const itemsToCheck = tenantId
      ? allItems.filter((item: CachedRecord) => item.tenant_id === tenantId || item.branch_id === tenantId)
      : allItems;

    // Ordenar por último acceso (más antiguos primero)
    const sorted = itemsToCheck.sort((a: CachedRecord, b: CachedRecord) => {
      const aTime = (a._last_accessed as number) || 0;
      const bTime = (b._last_accessed as number) || 0;
      return aTime - bTime; // Ascendente: más antiguos primero
    });

    // Eliminar los más antiguos
    const toEvict = sorted.slice(0, excess);
    const tx = db.transaction(storeName, 'readwrite');

    await Promise.all(
      toEvict.map((item: CachedRecord) => tx.store.delete(item.id))
    );
    
    await tx.done;

    logDebug(`[LRU] Evictados ${toEvict.length} items del store '${storeName}'`);
    return toEvict.length;
  } catch (error) {
    logWarn(`[LRU] Error al evictar items de '${storeName}':`, error);
    return 0;
  }
}

/**
 * Verificar y aplicar límites LRU antes de agregar items
 * 
 * @param db Instancia de IndexedDB
 * @param storeName Nombre del store
 * @param itemsToAdd Cantidad de items que se van a agregar
 * @param tenantId ID del tenant (opcional, para filtrar)
 */
export async function checkAndEvictIfNeeded(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: IDBPDatabase<any>,
  storeName: StoreName,
  itemsToAdd: number,
  tenantId?: string
): Promise<void> {
  const limit = getStoreLimit(storeName);
  
  // Contar items actuales
  const currentCount = await db.count(storeName);
  
  // Si agregar estos items excedería el límite, evictar primero
  if (currentCount + itemsToAdd > limit) {
    const targetCount = limit - itemsToAdd;
    await evictLRUItems(db, storeName, currentCount, Math.max(0, targetCount), tenantId);
  }
}

/**
 * Actualizar timestamp de acceso cuando se lee un item
 * 
 * @param db Instancia de IndexedDB
 * @param storeName Nombre del store
 * @param itemId ID del item
 */
export async function updateAccessTime(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: IDBPDatabase<any>,
  storeName: StoreName,
  itemId: string
): Promise<void> {
  try {
    const item = await db.get(storeName, itemId);
    if (item) {
      const updated = addAccessTimestamp(item);
      await db.put(storeName, updated);
    }
  } catch {
    // Silenciar errores de actualización de timestamp (no crítico)
    logDebug(`[LRU] No se pudo actualizar timestamp para ${storeName}/${itemId}`);
  }
}

/**
 * Limpiar timestamps de acceso antiguos (mantenimiento)
 * Solo mantener timestamps de los últimos 30 días
 */
export async function cleanupOldTimestamps(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: IDBPDatabase<any>,
  storeName: StoreName
): Promise<number> {
  const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
  let cleaned = 0;

  try {
    const allItems = await db.getAll(storeName);
    const tx = db.transaction(storeName, 'readwrite');

    for (const item of allItems) {
      if (item._last_accessed && item._last_accessed < thirtyDaysAgo) {
        // Eliminar timestamp antiguo (opcional, para ahorrar espacio)
        delete item._last_accessed;
        await tx.store.put(item);
        cleaned++;
      }
    }

    await tx.done;
    if (cleaned > 0) {
      logDebug(`[LRU] Limpiados ${cleaned} timestamps antiguos de '${storeName}'`);
    }
  } catch (error) {
    logWarn(`[LRU] Error al limpiar timestamps de '${storeName}':`, error);
  }

  return cleaned;
}

/**
 * Obtener estadísticas de uso del caché
 */
export async function getCacheStats(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: IDBPDatabase<any>,
  storeName: StoreName
): Promise<{
  count: number;
  limit: number;
  usagePercent: number;
  oldestAccess: number | null;
  newestAccess: number | null;
}> {
  const limit = getStoreLimit(storeName);
  const count = await db.count(storeName);
  
  const allItems = await db.getAll(storeName);
  const accessTimes = allItems
    .map((item: CachedRecord) => item._last_accessed as number | undefined)
    .filter((time: number | undefined) => time != null)
    .sort((a: number, b: number) => a - b);

  return {
    count,
    limit,
    usagePercent: limit > 0 ? Math.round((count / limit) * 100) : 0,
    oldestAccess: accessTimes[0] || null,
    newestAccess: accessTimes[accessTimes.length - 1] || null,
  };
}
